import type { JSX, ReactNode } from "react";
import { cn } from "@openreel/ui";
import { SURFACE_BG } from "./surface";
import type { AuthUser } from "./types";

export interface StepShellProps {
  /** Small muted line above the heading. Omitted on the welcome interstitial. */
  kicker?: string;
  heading: string;
  subheading?: string;
  /** Drawn top-right. Omitted while signed out. */
  user?: AuthUser | null;
  /** The chip row, input, or whatever the step asks for. */
  children?: ReactNode;
  /** Back / Continue, or the sign-in buttons. */
  footer?: ReactNode;
  /**
   * Announced politely when the step changes, so screen-reader users hear the
   * new question without the focus jumping.
   */
  announcement?: string;
  error?: string | null;
}

function Wordmark(): JSX.Element {
  return (
    <span className="select-none font-display text-[30px] leading-none tracking-tight text-white">
      flshy
    </span>
  );
}

function UserAvatar({ user }: { user: AuthUser }): JSX.Element {
  const initial = (user.name?.trim() || user.email).charAt(0).toUpperCase();

  return (
    <span
      className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full border border-white/15 bg-[#1c1c1c] text-[13px] font-medium text-[#c9c9c9]"
      title={user.email}
    >
      {user.avatarUrl ? (
        <img
          src={user.avatarUrl}
          alt=""
          className="h-full w-full object-cover"
          referrerPolicy="no-referrer"
        />
      ) : (
        <span aria-hidden>{initial}</span>
      )}
      <span className="sr-only">{user.name ?? user.email}</span>
    </span>
  );
}

export function StepShell({
  kicker,
  heading,
  subheading,
  user,
  children,
  footer,
  announcement,
  error,
}: StepShellProps): JSX.Element {
  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex flex-col overflow-y-auto text-white",
        SURFACE_BG,
      )}
    >
      {/* Decorative: the landing page's footer artwork, ghosted. Sits under
          everything, and is announced to nobody. */}
      <div className="onboarding-backdrop" aria-hidden="true" />

      <header className="relative z-10 flex shrink-0 items-center justify-between px-6 py-5 sm:px-8">
        <Wordmark />
        {user ? <UserAvatar user={user} /> : <span className="h-8 w-8" />}
      </header>

      <main className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 pb-16">
        <div className="w-full max-w-3xl text-center">
          {kicker ? (
            <p className="text-[14px] font-normal text-[#8f8f8f]">{kicker}</p>
          ) : null}

          <h1
            className={cn(
              "font-sans text-[clamp(2.25rem,6vw,3.75rem)] font-extralight leading-[1.08] tracking-[-0.02em] text-white",
              kicker ? "mt-3" : "mt-0",
            )}
          >
            {heading}
          </h1>

          {subheading ? (
            <p className="mt-4 text-[16px] text-[#8f8f8f]">{subheading}</p>
          ) : null}

          {children ? (
            <div className="mt-12 flex justify-center">{children}</div>
          ) : null}

          {error ? (
            <p role="alert" className="mt-6 text-[13px] text-[#ff8a8a]">
              {error}
            </p>
          ) : null}

          {footer ? <div className="mt-10">{footer}</div> : null}
        </div>
      </main>

      <p aria-live="polite" className="sr-only">
        {announcement ?? ""}
      </p>
    </div>
  );
}
